import { useCallback } from "react";
import { Barcode } from "lucide-react";
import { getUpcMetrics } from "@/api/client";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { StatCard } from "@/components/StatCard";
import { TimeSeriesAreaChart } from "@/components/TimeSeriesAreaChart";
import { UpcListInput } from "@/components/UpcListInput";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatCompact, formatCurrency, formatNumber } from "@/lib/format";

// A known-active trio (the Coca-Cola items from the Looker dashboard), for
// exploring the page without hunting down UPCs first.
const SAMPLE_UPCS = ["0001200080994", "0001200000230", "0001200000129"];

type StoreRow = { storeName: string; units: number };
type StoreUpcRow = {
  storeName: string;
  upc: string;
  minPrice: number;
  units: number;
};

const storeColumns: Column<StoreRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

const storeUpcColumns: Column<StoreUpcRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Lowest price",
    align: "right",
    cell: (r) => formatCurrency(r.minPrice),
    sortValue: (r) => r.minPrice,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

export function UpcMetricsPage() {
  const filters = useProductFilterOptions();
  const [upcsDraft, setUpcs, upcs] = useDebouncedUrlFilter("upcs");
  const [dmas, setDmas, dma] = useUrlListFilter("dma");

  const fetchMetrics = useCallback(
    (token: string) =>
      getUpcMetrics(token, {
        upcs: upcs || undefined,
        dma,
      }),
    [upcs, dma],
  );
  const state = useApiData(fetchMetrics);
  // null = no UPCs entered yet (the endpoint requires a list)
  const data = state.status === "ready" ? state.data : null;

  const totalUnits = data
    ? data.unitsByDay.reduce((sum, d) => sum + d.units, 0)
    : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">UPC metrics</h1>
        <p className="text-sm text-muted-foreground">
          Sales for a specific set of UPCs over the last 30 days
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <UpcListInput value={upcsDraft} onApply={setUpcs} />
        <MultiFilterSelect
          label="DMA"
          allLabel="All DMAs"
          values={dmas}
          onChange={setDmas}
          disabled={filters.disabled}
          options={filters.dmaOptions}
        />
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : state.status === "ready" && data === null ? (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-6 py-14 text-center">
          <div
            className="mb-1 flex h-11 w-11 items-center justify-center rounded-full"
            style={{ background: "var(--viz-meter-track)" }}
          >
            <Barcode
              className="h-5 w-5"
              style={{ color: "var(--viz-series-1)" }}
              aria-hidden
            />
          </div>
          <p className="text-sm font-medium">No UPCs selected</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Add UPCs above to see daily units, store rankings, and lowest
            prices — or start from a sample set.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setUpcs(SAMPLE_UPCS.join(","))}
          >
            Try sample UPCs
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Total units"
              value={totalUnits === null ? null : formatCompact(totalUnits)}
            />
            <StatCard
              label="Stores with sales"
              value={data && formatNumber(data.byStore.length)}
            />
            <StatCard
              label="UPCs matched"
              value={data && formatNumber(data.upcs.length)}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Units over time</CardTitle>
              <CardDescription>Daily units</CardDescription>
            </CardHeader>
            <CardContent>
              {!data ? (
                <ChartSkeleton height={260} />
              ) : (
                <TimeSeriesAreaChart
                  data={data.unitsByDay.map((d) => ({
                    day: d.day,
                    value: d.units,
                  }))}
                  seriesName="Units"
                />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <CardTitle>Units by store</CardTitle>
                {data ? (
                  <CsvDownloadButton
                    name="upc-units-by-store"
                    columns={storeColumns}
                    rows={data.byStore}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={storeColumns}
                    rows={data.byStore}
                    rowKey={(r) => r.storeName}
                    maxHeight="28rem"
                    emptyMessage="No sales in range"
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>UPCs by store</CardTitle>
                  <CardDescription>
                    Lowest observed price per store per UPC
                  </CardDescription>
                </div>
                {data ? (
                  <CsvDownloadButton
                    name="upc-units-by-store-and-upc"
                    columns={storeUpcColumns}
                    rows={data.byStoreUpc}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={storeUpcColumns}
                    rows={data.byStoreUpc}
                    rowKey={(r) => `${r.storeName}-${r.upc}`}
                    maxHeight="28rem"
                    emptyMessage="No sales in range"
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
